import { useState } from "react";
import {
  upsertPropFirmPhase,
  deletePropFirmPhase,
  useAction,
} from "wasp/client/operations";
import { Button } from "../../../client/components/ui/button";
import { Input } from "../../../client/components/ui/input";
import { SectionHeader } from "./SectionHeader";
import { SELECT_CLASS } from "./formStyles";
import { useToast } from "../../../client/hooks/use-toast";
import {
  Plus,
  Pencil,
  Trash2,
  Target,
  TrendingDown,
  Wallet,
  Settings2,
} from "lucide-react";

// ─── Types ───────────────────────────────────────────────────────────────────

interface PhaseForm {
  id?: string;
  name: string;
  phaseType: "evaluation" | "evaluation_2" | "funded" | "funded_variant" | "live";
  sortOrder: number;
  drawdownType: "" | "intraday" | "eod" | "static" | "trailing";
  maxDrawdownPercent: string;
  maxDrawdownAmount: string;
  maxDailyLossPercent: string;
  maxDailyLossAmount: string;
  profitTargetPercent: string;
  profitTargetAmount: string;
  minTradingDays: string;
  payoutThresholdAmount: string;
  payoutCooldownDays: string;
  payoutSplitPercent: string;
  maxPayoutAmount: string;
  firstPayoutDays: string;
  consistencyRulePercent: string;
  maxContracts: string;
  newsTrading: boolean | null;
  overnightHolding: boolean | null;
  weekendHolding: boolean | null;
  minWinningDaysForPayout: string;
  minWinningDayProfit: string;
}

const emptyPhaseForm = (allowEvaluation: boolean = true): PhaseForm => ({
  name: "",
  phaseType: allowEvaluation ? "evaluation" : "funded",
  sortOrder: 0,
  drawdownType: "",
  maxDrawdownPercent: "",
  maxDrawdownAmount: "",
  maxDailyLossPercent: "",
  maxDailyLossAmount: "",
  profitTargetPercent: "",
  profitTargetAmount: "",
  minTradingDays: "",
  payoutThresholdAmount: "",
  payoutCooldownDays: "",
  payoutSplitPercent: "",
  maxPayoutAmount: "",
  firstPayoutDays: "",
  consistencyRulePercent: "",
  maxContracts: "",
  newsTrading: null,
  overnightHolding: null,
  weekendHolding: null,
  minWinningDaysForPayout: "",
  minWinningDayProfit: "",
});

// ─── PhaseFormFields ──────────────────────────────────────────────────────────

const PHASE_TYPE_OPTIONS = [
  { value: "evaluation", label: "Évaluation (Challenge)" },
  { value: "evaluation_2", label: "Évaluation Phase 2" },
  { value: "funded", label: "Funded (PA)" },
  { value: "funded_variant", label: "Funded variante" },
  // Programme live (argent réel, hôte Tradovate live) : règles des comptes "live".
  { value: "live", label: "Live (programme réel)" },
];

const PHASE_DRAWDOWN_TYPE_OPTIONS = [
  { value: "", label: "Aucun" },
  { value: "trailing", label: "Trailing" },
  { value: "static", label: "Perte totale (static)" },
  { value: "eod", label: "Perte totale (EOD)" },
  { value: "intraday", label: "Perte journalière" },
];

function PhaseFormFields({
  form,
  setForm,
  allowEvaluation,
}: {
  form: PhaseForm;
  setForm: (f: PhaseForm) => void;
  /**
   * Direct funded n'a pas de challenge : les types "evaluation" /
   * "evaluation_2" sont retires du select. Une phase EXISTANTE de ce type
   * (type de compte bascule en direct funded apres coup) n'est ni supprimee
   * ni masquee ailleurs dans l'UI -- seulement reinjectee ici pour que le
   * select ne se retrouve pas a afficher une valeur absente de ses options.
   */
  allowEvaluation: boolean;
}) {
  function set(key: keyof PhaseForm, value: any) {
    setForm({ ...form, [key]: value });
  }

  const options = allowEvaluation
    ? PHASE_TYPE_OPTIONS
    : PHASE_TYPE_OPTIONS.filter((o) => !o.value.startsWith("evaluation"));
  // Le select ne doit jamais afficher une valeur absente de ses options : une
  // phase d'evaluation existante, editee apres bascule du type de compte en
  // direct funded, doit rester visible et selectionnee telle quelle.
  const selectOptions = options.some((o) => o.value === form.phaseType)
    ? options
    : [...options, ...PHASE_TYPE_OPTIONS.filter((o) => o.value === form.phaseType)];

  return (
    <div className="space-y-3 text-xs">
      {/* Identité */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-muted-foreground block mb-1">Nom *</label>
          <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="ex: Challenge" className="h-8 text-xs" />
        </div>
        <div>
          <label className="text-muted-foreground block mb-1">Type de phase</label>
          <select
            value={form.phaseType}
            onChange={(e) => set("phaseType", e.target.value)}
            className={SELECT_CLASS}
          >
            {selectOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-muted-foreground block mb-1">Sort Order</label>
          <Input type="number" value={form.sortOrder} onChange={(e) => set("sortOrder", parseInt(e.target.value) || 0)} className="h-8 text-xs" />
        </div>
      </div>

      {/* Drawdown */}
      <SectionHeader icon={<TrendingDown className="w-3 h-3" />} label="Drawdown" />
      <div>
        <label className="text-muted-foreground block mb-1">Type de drawdown</label>
        <select
          value={form.drawdownType}
          onChange={(e) => set("drawdownType", e.target.value)}
          className={SELECT_CLASS}
        >
          {PHASE_DRAWDOWN_TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
      {form.drawdownType && (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-muted-foreground block mb-1">% drawdown max</label>
            <Input value={form.maxDrawdownPercent} onChange={(e) => set("maxDrawdownPercent", e.target.value)} placeholder="ex: 4" type="text" inputMode="decimal" className="h-8 text-xs" />
          </div>
          <div>
            <label className="text-muted-foreground block mb-1">$ drawdown max</label>
            <Input value={form.maxDrawdownAmount} onChange={(e) => set("maxDrawdownAmount", e.target.value)} placeholder="ex: 2000" type="text" inputMode="decimal" className="h-8 text-xs" />
          </div>
          {form.drawdownType !== "trailing" && (
            <>
              <div>
                <label className="text-muted-foreground block mb-1">% perte journalière max</label>
                <Input value={form.maxDailyLossPercent} onChange={(e) => set("maxDailyLossPercent", e.target.value)} placeholder="ex: 2" type="text" inputMode="decimal" className="h-8 text-xs" />
              </div>
              <div>
                <label className="text-muted-foreground block mb-1">$ perte journalière max</label>
                <Input value={form.maxDailyLossAmount} onChange={(e) => set("maxDailyLossAmount", e.target.value)} placeholder="ex: 1000" type="text" inputMode="decimal" className="h-8 text-xs" />
              </div>
            </>
          )}
        </div>
      )}

      {/* Profit Target */}
      <SectionHeader icon={<Target className="w-3 h-3" />} label="Profit Target" />
      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="text-muted-foreground block mb-1">%</label>
          <Input value={form.profitTargetPercent} onChange={(e) => set("profitTargetPercent", e.target.value)} placeholder="ex: 6" type="text" inputMode="decimal" className="h-8 text-xs" />
        </div>
        <div>
          <label className="text-muted-foreground block mb-1">$ montant</label>
          <Input value={form.profitTargetAmount} onChange={(e) => set("profitTargetAmount", e.target.value)} placeholder="ex: 3000" type="text" inputMode="decimal" className="h-8 text-xs" />
        </div>
        <div>
          <label className="text-muted-foreground block mb-1">Jours min</label>
          <Input value={form.minTradingDays} onChange={(e) => set("minTradingDays", e.target.value)} placeholder="ex: 5" type="text" inputMode="numeric" className="h-8 text-xs" />
        </div>
      </div>

      {/* Payout */}
      <SectionHeader icon={<Wallet className="w-3 h-3" />} label="Payout" />
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-muted-foreground block mb-1">Threshold ($)</label>
          <Input value={form.payoutThresholdAmount} onChange={(e) => set("payoutThresholdAmount", e.target.value)} placeholder="ex: 125" type="text" inputMode="decimal" className="h-8 text-xs" />
        </div>
        <div>
          <label className="text-muted-foreground block mb-1">Cooldown (jours)</label>
          <Input value={form.payoutCooldownDays} onChange={(e) => set("payoutCooldownDays", e.target.value)} placeholder="ex: 14" type="text" inputMode="numeric" className="h-8 text-xs" />
        </div>
        <div>
          <label className="text-muted-foreground block mb-1">Split % trader</label>
          <Input value={form.payoutSplitPercent} onChange={(e) => set("payoutSplitPercent", e.target.value)} placeholder="ex: 90" type="text" inputMode="decimal" className="h-8 text-xs" />
        </div>
        <div>
          <label className="text-muted-foreground block mb-1">Max payout ($)</label>
          <Input value={form.maxPayoutAmount} onChange={(e) => set("maxPayoutAmount", e.target.value)} placeholder="ex: 5000" type="text" inputMode="decimal" className="h-8 text-xs" />
        </div>
        <div>
          <label className="text-muted-foreground block mb-1">Premier payout (jours)</label>
          <Input value={form.firstPayoutDays} onChange={(e) => set("firstPayoutDays", e.target.value)} placeholder="ex: 14" type="text" inputMode="numeric" className="h-8 text-xs" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Jours gagnants min (payout)</label>
          <Input value={form.minWinningDaysForPayout} onChange={(e) => set("minWinningDaysForPayout", e.target.value)} placeholder="ex: 5" type="number" className="h-8 text-xs" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Profit min par jour ($)</label>
          <Input value={form.minWinningDayProfit} onChange={(e) => set("minWinningDayProfit", e.target.value)} placeholder="ex: 150" type="number" className="h-8 text-xs" />
        </div>
      </div>

      {/* Règles */}
      <SectionHeader icon={<Settings2 className="w-3 h-3" />} label="Règles & Contraintes" />
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-muted-foreground block mb-1">Consistency Rule (%)</label>
          <Input value={form.consistencyRulePercent} onChange={(e) => set("consistencyRulePercent", e.target.value)} placeholder="ex: 50" type="text" inputMode="decimal" className="h-8 text-xs" />
        </div>
        <div>
          <label className="text-muted-foreground block mb-1">Max Contracts</label>
          <Input value={form.maxContracts} onChange={(e) => set("maxContracts", e.target.value)} placeholder="ex: 10" type="text" inputMode="numeric" className="h-8 text-xs" />
        </div>
      </div>
    </div>
  );
}

// ─── PhaseEditor ──────────────────────────────────────────────────────────────

export function PhaseEditor({
  tierId,
  phases,
  allowEvaluation,
  onPhaseSaved,
  onPhaseDeleted,
  onBeforePhaseWrite,
}: {
  tierId: string;
  phases: any[];
  /** Direct funded (voir `pricingModel.ts`) : pas de phase d'evaluation possible. */
  allowEvaluation: boolean;
  onPhaseSaved: () => void;
  onPhaseDeleted: () => void;
  /**
   * Sauvegarde le template AVANT toute ecriture de phase, et dit si l'ecriture
   * peut avoir lieu.
   *
   * POURQUOI : ce composant est monte DANS le `<form>` du template, dont la
   * saisie ne vit qu'en memoire React jusqu'au submit. Ecrire une phase
   * declenche `onPhaseSaved` -> refresh -> reinjection du template depuis la
   * base, ce qui ECRASAIT toute modification non encore soumise. Un associe a
   * perdu son travail ainsi. Detail dans `propFirmPhaseGuard.ts`.
   */
  onBeforePhaseWrite: () => Promise<boolean>;
}) {
  const { toast } = useToast();
  const upsertPhase = useAction(upsertPropFirmPhase);
  const deletePhase = useAction(deletePropFirmPhase);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingNew, setEditingNew] = useState(false);
  const [form, setForm] = useState<PhaseForm>(emptyPhaseForm(allowEvaluation));
  const [saving, setSaving] = useState(false);

  function startEdit(phase: any) {
    setForm({
      id: phase.id,
      name: phase.name,
      phaseType: phase.phaseType,
      sortOrder: phase.sortOrder,
      drawdownType: phase.drawdownType ?? "",
      maxDrawdownPercent: phase.maxDrawdownPercent?.toString() ?? "",
      maxDrawdownAmount: phase.maxDrawdownAmount?.toString() ?? "",
      maxDailyLossPercent: phase.maxDailyLossPercent?.toString() ?? "",
      maxDailyLossAmount: phase.maxDailyLossAmount?.toString() ?? "",
      profitTargetPercent: phase.profitTargetPercent?.toString() ?? "",
      profitTargetAmount: phase.profitTargetAmount?.toString() ?? "",
      minTradingDays: phase.minTradingDays?.toString() ?? "",
      payoutThresholdAmount: phase.payoutThresholdAmount?.toString() ?? "",
      payoutCooldownDays: phase.payoutCooldownDays?.toString() ?? "",
      payoutSplitPercent: phase.payoutSplitPercent?.toString() ?? "",
      maxPayoutAmount: phase.maxPayoutAmount?.toString() ?? "",
      firstPayoutDays: phase.firstPayoutDays?.toString() ?? "",
      consistencyRulePercent: phase.consistencyRulePercent?.toString() ?? "",
      maxContracts: phase.maxContracts?.toString() ?? "",
      newsTrading: phase.newsTrading ?? null,
      overnightHolding: phase.overnightHolding ?? null,
      weekendHolding: phase.weekendHolding ?? null,
      minWinningDaysForPayout: phase.minWinningDaysForPayout?.toString() ?? "",
      minWinningDayProfit: phase.minWinningDayProfit?.toString() ?? "",
    });
    setExpandedId(phase.id);
    setEditingNew(false);
  }

  function startNew() {
    // Une deuxième phase à `sortOrder: 0` ferait égalité avec la première :
    // l'ordre affiché (et celui de `resolvePhase` entre phases de même type)
    // deviendrait indéfini. On la place à la suite, l'admin peut réordonner.
    setForm({ ...emptyPhaseForm(allowEvaluation), sortOrder: phases.length });
    setEditingNew(true);
    setExpandedId(null);
  }

  async function handleSave() {
    setSaving(true);
    try {
      // Le template d'abord : sinon le refresh qui suit cette ecriture
      // effacerait la saisie en cours du formulaire parent.
      if (!(await onBeforePhaseWrite())) return;

      await upsertPhase({
        id: form.id,
        tierId,
        name: form.name,
        phaseType: form.phaseType,
        sortOrder: form.sortOrder,
        drawdownType: form.drawdownType || null,
        maxDrawdownPercent: form.maxDrawdownPercent ? parseFloat(form.maxDrawdownPercent) : null,
        maxDrawdownAmount: form.maxDrawdownAmount ? parseFloat(form.maxDrawdownAmount) : null,
        maxDailyLossPercent: form.maxDailyLossPercent ? parseFloat(form.maxDailyLossPercent) : null,
        maxDailyLossAmount: form.maxDailyLossAmount ? parseFloat(form.maxDailyLossAmount) : null,
        profitTargetPercent: form.profitTargetPercent ? parseFloat(form.profitTargetPercent) : null,
        profitTargetAmount: form.profitTargetAmount ? parseFloat(form.profitTargetAmount) : null,
        minTradingDays: form.minTradingDays ? parseInt(form.minTradingDays) : null,
        payoutThresholdAmount: form.payoutThresholdAmount ? parseFloat(form.payoutThresholdAmount) : null,
        payoutCooldownDays: form.payoutCooldownDays ? parseInt(form.payoutCooldownDays) : null,
        payoutSplitPercent: form.payoutSplitPercent ? parseFloat(form.payoutSplitPercent) : null,
        maxPayoutAmount: form.maxPayoutAmount ? parseFloat(form.maxPayoutAmount) : null,
        firstPayoutDays: form.firstPayoutDays ? parseInt(form.firstPayoutDays) : null,
        consistencyRulePercent: form.consistencyRulePercent ? parseFloat(form.consistencyRulePercent) : null,
        maxContracts: form.maxContracts ? parseInt(form.maxContracts) : null,
        newsTrading: form.newsTrading,
        overnightHolding: form.overnightHolding,
        weekendHolding: form.weekendHolding,
        minWinningDaysForPayout: form.minWinningDaysForPayout ? parseInt(form.minWinningDaysForPayout) : null,
        minWinningDayProfit: form.minWinningDayProfit ? parseFloat(form.minWinningDayProfit) : null,
      });
      setEditingNew(false);
      setExpandedId(null);
      onPhaseSaved();
    } catch (e: any) {
      // Sans ce `catch`, un echec d'enregistrement ne montrait RIEN : le
      // formulaire se contentait de rester ouvert, et l'admin pouvait croire
      // sa phase enregistree.
      toast({
        title: "Erreur",
        description: e?.message ?? "La phase n'a pas pu etre enregistree.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm("Supprimer cette phase ?")) return;
    // Meme raison qu'a l'enregistrement : la suppression rafraichit le
    // template et ecraserait la saisie en cours.
    if (!(await onBeforePhaseWrite())) return;
    try {
      await deletePhase({ id });
      onPhaseDeleted();
    } catch (e: any) {
      toast({
        title: "Erreur",
        description: e?.message ?? "La phase n'a pas pu etre supprimee.",
        variant: "destructive",
      });
    }
  }

  const sorted = [...phases].sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <div className="mt-4 border-t border-border/40 pt-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Phases</span>
        <Button type="button" size="sm" variant="outline" onClick={startNew} className="h-6 text-xs">
          <Plus className="w-3 h-3 mr-1" /> Ajouter une phase
        </Button>
      </div>

      {sorted.map((phase) => (
        <div key={phase.id} className="mb-2 border border-border/30 rounded-md overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 bg-muted/20">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium">{phase.name}</span>
              <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{phase.phaseType}</span>
              {phase.drawdownType === "trailing" && (
                <span className="text-xs text-primary">
                  trailing {phase.maxDrawdownAmount ? `$${phase.maxDrawdownAmount}` : phase.maxDrawdownPercent ? `${phase.maxDrawdownPercent}%` : ""}
                </span>
              )}
              {phase.drawdownType && phase.drawdownType !== "trailing" && (
                <span className="text-xs text-muted-foreground">
                  {phase.drawdownType} {phase.maxDrawdownAmount ? `$${phase.maxDrawdownAmount}` : ""}
                </span>
              )}
            </div>
            <div className="flex gap-1">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-6 w-6 p-0"
                onClick={() => expandedId === phase.id ? setExpandedId(null) : startEdit(phase)}
              >
                <Pencil className="w-3 h-3" />
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-6 w-6 p-0 text-destructive"
                onClick={() => handleDelete(phase.id)}
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </div>
          {expandedId === phase.id && (
            <div className="p-3 space-y-3 bg-background">
              <PhaseFormFields form={form} setForm={setForm} allowEvaluation={allowEvaluation} />
              <div className="flex gap-2 justify-end">
                <Button type="button" size="sm" variant="outline" onClick={() => setExpandedId(null)}>Annuler</Button>
                <Button type="button" size="sm" onClick={handleSave} disabled={saving}>
                  {saving ? "Saving..." : "Enregistrer"}
                </Button>
              </div>
            </div>
          )}
        </div>
      ))}

      {editingNew && (
        <div className="border border-primary/30 rounded-md p-3 space-y-3 bg-background">
          <p className="text-xs font-semibold text-primary">Nouvelle phase</p>
          <PhaseFormFields form={form} setForm={setForm} allowEvaluation={allowEvaluation} />
          <div className="flex gap-2 justify-end">
            <Button type="button" size="sm" variant="outline" onClick={() => setEditingNew(false)}>Annuler</Button>
            <Button type="button" size="sm" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Enregistrer"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
